import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, Grid, Button, Menu, MenuItem, ListItemIcon, ListItemText, CircularProgress, Alert } from '@mui/material';
import { Celebration, KeyboardArrowDown, AcUnit, AutoAwesome } from '@mui/icons-material';
import ApiService from '../../../services/api';
import logger from '../../../utils/logger';
import './SpecialDaysEDMs.css';

const staticTemplates = [
]

const FestivalEDMsSelection = () => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [festivals, setFestivals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const open = Boolean(anchorEl);

  useEffect(() => {
    fetchFestivals();
  }, []);

  const fetchFestivals = async () => {
    try {
      // Fetch active festivals from backend
      const res = await ApiService.get('/customer/festival/get-all-active');
      const data = res?.data || res;

      if (Array.isArray(data)) {
        // Map backend data to UI format
        const dynamicFestivals = data.map(f => ({
          label: f.name,
          emoji: f.emoji || '🎉',
          color: getRandomColor(), // Assign random color or derive from somewhere
          isStatic: false,
          original: f
        }));

        // Combine static templates with dynamic ones (you can choose to replace or append)
        // For now, let's prioritize dynamic ones or show both.
        // If dynamic ones cover the static ones, we might want to filter duplicates.
        // Let's just append for now.
        setFestivals([...staticTemplates, ...dynamicFestivals]);
      } else {
        setFestivals(staticTemplates);
      }
    } catch (err) {
      logger.error('Failed to fetch festivals:', err);
      // Fallback to static templates on error
      setFestivals(staticTemplates);
      // Optional: setError('Failed to load festivals');
    } finally {
      setLoading(false);
    }
  };

  const getRandomColor = () => {
    const colors = ['#f59e0b', '#0b91d6', '#ec4899', '#10b981', '#6366f1', '#8b5cf6'];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    handleClose();
    // Pass the selected festival logic
    // For dynamic festivals, we might want to pass more ID or details
    navigate('../festival-edms', {
      state: {
        selectedFestival: template.label,
        festivalData: template.isStatic ? null : template.original
      }
    });
  };

  return (
    <Box sx={{ p: 4, background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{
        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
        borderRadius: '16px',
        padding: '32px 40px',
        marginBottom: '32px',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 4px 24px rgba(245, 158, 11, 0.3)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '-50%',
          right: '-5%',
          width: '200px',
          height: '200px',
          background: 'rgba(255,255,255,0.1)',
          borderRadius: '50%'
        }
      }}>
        <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Celebration sx={{ fontSize: 36, color: 'white' }} />
          <Box>
            <Typography sx={{ fontSize: '28px', fontWeight: 700, color: 'white', fontFamily: 'Inter, sans-serif' }}>
              Festival EDMs
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '15px', fontFamily: 'Inter, sans-serif' }}>
              Create beautiful designs for festive occasions
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Selection Section */}
      <Paper sx={{
        p: 5,
        borderRadius: '16px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)',
        background: 'white',
        textAlign: 'center'
      }}>
        <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#1e293b', mb: 1, fontFamily: 'Inter, sans-serif' }}>
          Select Your Festival
        </Typography>
        <Typography sx={{ fontSize: '15px', color: '#64748b', mb: 4, maxWidth: 500, mx: 'auto' }}>
          Choose a festival to create custom marketing materials
        </Typography>

        {loading ? (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Dropdown Button */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
              <Button
                onClick={handleClick}
                variant="outlined"
                endIcon={<KeyboardArrowDown />}
                sx={{
                  minWidth: 280,
                  py: 1.5,
                  px: 3,
                  borderRadius: '12px',
                  borderColor: '#e2e8f0',
                  color: selectedTemplate ? '#1e293b' : '#64748b',
                  fontWeight: 500,
                  fontSize: '15px',
                  textTransform: 'none',
                  justifyContent: 'space-between',
                  '&:hover': {
                    borderColor: '#f59e0b',
                    background: '#fffbeb'
                  }
                }}
              >
                {selectedTemplate?.label || 'Select a festival'}
              </Button>
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                PaperProps={{
                  sx: {
                    minWidth: 280,
                    borderRadius: '12px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                    mt: 1,
                    maxHeight: 400
                  }
                }}
              >
                {festivals.map((template, idx) => {
                  const IconComponent = template.isStatic ? template.icon : null;
                  return (
                    <MenuItem
                      key={`${template.label}-${idx}`}
                      onClick={() => handleTemplateSelect(template)}
                      sx={{
                        py: 1.5,
                        '&:hover': {
                          background: `${template.color}15`
                        }
                      }}
                    >
                      <ListItemIcon>
                        {IconComponent ? (
                          <IconComponent sx={{ color: template.color }} />
                        ) : (
                          <Typography fontSize="20px">{template.emoji}</Typography>
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={template.label}
                        primaryTypographyProps={{
                          fontWeight: 500,
                          fontSize: '14px'
                        }}
                      />
                    </MenuItem>
                  );
                })}
              </Menu>
            </Box>

            {/* Quick Selection Cards */}
            <Typography sx={{ fontSize: '14px', color: '#94a3b8', mb: 3 }}>
              Or quickly select from popular festivals:
            </Typography>
            <Grid container spacing={2} justifyContent="center">
              {festivals.map((template, idx) => {
                const IconComponent = template.isStatic ? template.icon : null;
                return (
                  <Grid item xs={6} sm={3} md={2} key={`${template.label}-${idx}`}>
                    <Paper
                      onClick={() => handleTemplateSelect(template)}
                      sx={{
                        p: 3,
                        borderRadius: '12px',
                        border: '1px solid #e2e8f0',
                        cursor: 'pointer',
                        transition: 'all 0.25s ease',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        '&:hover': {
                          borderColor: template.color,
                          transform: 'translateY(-4px)',
                          boxShadow: `0 12px 24px ${template.color}25`
                        }
                      }}
                    >
                      <Box sx={{
                        width: 48, height: 48, borderRadius: '12px',
                        bgcolor: `${template.color}15`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        mx: 'auto', mb: 2
                      }}>
                        {IconComponent ? (
                          <IconComponent sx={{ fontSize: 24, color: template.color }} />
                        ) : (
                          <Typography fontSize="24px">{template.emoji}</Typography>
                        )}
                      </Box>
                      <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                        {template.label}
                      </Typography>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default FestivalEDMsSelection;
